/**
 * useSavedStations Hook
 * 
 * 管理喜愛車站的增刪查改
 * 支援所有路線類型（MTR、LRT、MTRBus）
 */

import { useState, useEffect, useCallback } from 'react';
import { getItem, setItem } from '../utils/localStorage';
import { performMigration, needsMigration } from '../utils/storageMigration';

const STORAGE_KEY = 'savedStations';

const parseMTRBusRouteKey = (route) => {
  const routeKey = String(route || '').trim();
  if (!routeKey) {
    return { routeKey: '', baseRoute: '', variantId: '' };
  }
  if (!routeKey.includes('::')) {
    return { routeKey, baseRoute: routeKey, variantId: '' };
  }
  const [baseRouteRaw, variantIdRaw] = routeKey.split('::');
  const baseRoute = String(baseRouteRaw || '').trim();
  const variantId = String(variantIdRaw || '').trim();
  return {
    routeKey: baseRoute && variantId ? `${baseRoute}::${variantId}` : routeKey,
    baseRoute: baseRoute || routeKey,
    variantId,
  };
};

const normalizeStationId = (stationId) => String(stationId || '').trim();

const extractRouteIdFromStopId = (stationId) => {
  const normalizedStopId = normalizeStationId(stationId);
  if (!normalizedStopId) return '';

  // 例子：K51-1-U010 / 506-D020 / K12-U030
  const directionalMatch = normalizedStopId.match(/^(.+)-[UD]\d+$/i);
  if (directionalMatch?.[1]) {
    return String(directionalMatch[1]).trim();
  }

  const genericMatch = normalizedStopId.match(/^([A-Z0-9-]+)-/i);
  if (genericMatch?.[1]) {
    return String(genericMatch[1]).trim();
  }

  return '';
};

const findVariantIdByStop = (routeNo, stationId) => {
  const normalizedRouteNo = String(routeNo || '').trim();
  const normalizedStopId = normalizeStationId(stationId);
  if (!normalizedRouteNo || !normalizedStopId) return '';

  const routeIdFromStop = extractRouteIdFromStopId(normalizedStopId);
  if (!routeIdFromStop) return '';

  const normalizedRouteId = String(routeIdFromStop).trim().toUpperCase();
  const normalizedBaseRoute = normalizedRouteNo.toUpperCase();
  if (
    normalizedRouteId === normalizedBaseRoute ||
    normalizedRouteId.startsWith(`${normalizedBaseRoute}-`)
  ) {
    return routeIdFromStop;
  }

  return '';
};

const getDefaultVariantId = (routeNo) => {
  const normalizedRouteNo = String(routeNo || '').trim();
  if (!normalizedRouteNo) return '';
  return normalizedRouteNo;
};

const migrateLegacyMTRBusData = (inputData) => {
  if (!inputData || typeof inputData !== 'object') return inputData || {};
  if (!inputData.MTRBus || typeof inputData.MTRBus !== 'object' || Array.isArray(inputData.MTRBus)) {
    return inputData;
  }

  const migratedMTRBus = {};
  let hasChanges = false;
  let upgradedCount = 0;

  const appendStationToKey = (targetKey, stationId) => {
    const normalizedKey = String(targetKey || '').trim();
    const normalizedStop = normalizeStationId(stationId);
    if (!normalizedKey || !normalizedStop) return;
    if (!migratedMTRBus[normalizedKey]) {
      migratedMTRBus[normalizedKey] = [];
    }
    if (!migratedMTRBus[normalizedKey].includes(normalizedStop)) {
      migratedMTRBus[normalizedKey].push(normalizedStop);
    }
  };

  Object.entries(inputData.MTRBus).forEach(([rawRouteKey, stationList]) => {
    const routeKey = String(rawRouteKey || '').trim();
    const stations = Array.isArray(stationList) ? stationList : [];
    if (!routeKey) return;

    // 已是新格式：保留並去重
    if (routeKey.includes('::')) {
      stations.forEach((stationId) => appendStationToKey(routeKey, stationId));
      return;
    }

    // 舊格式 routeNo：按 stop 精準映射到變體，找不到則回退第一個變體
    const routeNo = routeKey;
    upgradedCount += 1;
    const defaultVariantId = getDefaultVariantId(routeNo);
    stations.forEach((stationId) => {
      const matchedVariantId = findVariantIdByStop(routeNo, stationId) || defaultVariantId;
      if (matchedVariantId) {
        const normalizedMatched = String(matchedVariantId || '').trim().toUpperCase();
        const normalizedRoute = String(routeNo || '').trim().toUpperCase();
        const targetKey = normalizedMatched && normalizedMatched !== normalizedRoute
          ? `${routeNo}::${matchedVariantId}`
          : routeNo;
        appendStationToKey(targetKey, stationId);
      } else {
        // 無法映射時保留舊鍵，避免資料遺失
        appendStationToKey(routeNo, stationId);
      }
    });
    hasChanges = true;
  });

  if (!hasChanges) {
    return inputData;
  }

  const nextData = {
    ...inputData,
    MTRBus: migratedMTRBus,
  };
  setItem(STORAGE_KEY, nextData);
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Migration] MTRBus 舊版收藏升級完成，共轉換 ${upgradedCount} 個路線鍵值。`);
  }
  return nextData;
};

/**
 * 使用喜愛車站的 hook
 */
export const useSavedStations = () => {
  const [savedStations, setSavedStations] = useState(() => {
    // 檢查是否需要 migration
    if (needsMigration()) {
      const migrated = performMigration();
      return migrateLegacyMTRBusData(migrated || {});
    }
    
    // 讀取現有資料
    return migrateLegacyMTRBusData(getItem(STORAGE_KEY, {}));
  });

  // 初始化時執行 migration（如果需要的話）
  useEffect(() => {
    if (needsMigration()) {
      const migrated = performMigration();
      if (migrated) {
        setSavedStations(migrateLegacyMTRBusData(migrated));
      }
      return;
    }

    // 即使不用 performMigration，仍檢查一次舊 MTRBus key 升級
    const currentData = getItem(STORAGE_KEY, {});
    const migratedData = migrateLegacyMTRBusData(currentData);
    if (migratedData !== currentData) {
      setSavedStations(migratedData);
    }
  }, []);

  /**
   * 添加喜愛車站
   * @param {string} lineCode - 路線代碼（如 'TCL', 'LRT', 'MTRBus'）
   * @param {string} stationId - 車站 ID
   * @param {string} route - 路線（僅 MTRBus 需要，如 'K12'）
   */
  const addStation = useCallback((lineCode, stationId, route = null) => {
    setSavedStations(prev => {
      const newData = { ...prev };
      
      // MTRBus 需要特殊處理：按路線分組
      if (lineCode === 'MTRBus' && route) {
        const { routeKey } = parseMTRBusRouteKey(route);
        if (!routeKey) return newData;
        if (!newData[lineCode]) {
          newData[lineCode] = {};
        }
        if (!newData[lineCode][routeKey]) {
          newData[lineCode][routeKey] = [];
        }
        // 如果不存在，則添加
        if (!newData[lineCode][routeKey].includes(stationId)) {
          newData[lineCode][routeKey] = [...newData[lineCode][routeKey], stationId];
          setItem(STORAGE_KEY, newData);
        }
      } else {
        // 其他路線類型：使用陣列
        if (!newData[lineCode]) {
          newData[lineCode] = [];
        }
        // 如果不存在，則添加
        if (!newData[lineCode].includes(stationId)) {
          newData[lineCode] = [...newData[lineCode], stationId];
          setItem(STORAGE_KEY, newData);
        }
      }
      
      return newData;
    });
  }, []);

  /**
   * 移除喜愛車站
   * @param {string} lineCode - 路線代碼
   * @param {string} stationId - 車站 ID
   * @param {string} route - 路線（僅 MTRBus 需要，如 'K12'）
   */
  const removeStation = useCallback((lineCode, stationId, route = null) => {
    setSavedStations(prev => {
      const newData = { ...prev };
      
      // MTRBus 需要特殊處理：按路線分組
      if (lineCode === 'MTRBus' && route) {
        const { routeKey, baseRoute, variantId } = parseMTRBusRouteKey(route);
        const keysToRemove = [routeKey];
        if (variantId && baseRoute && baseRoute !== routeKey) {
          keysToRemove.push(baseRoute); // 兼容舊格式
        }

        let hasMutation = false;
        keysToRemove.forEach((key) => {
          if (!key || !newData[lineCode]?.[key]) return;
          hasMutation = true;
          newData[lineCode][key] = newData[lineCode][key].filter(id => id !== stationId);
          if (newData[lineCode][key].length === 0) {
            delete newData[lineCode][key];
          }
        });

        if (hasMutation) {
          // 如果 MTRBus 沒有路線了，刪除 MTRBus
          if (Object.keys(newData[lineCode] || {}).length === 0) {
            delete newData[lineCode];
          }

          setItem(STORAGE_KEY, newData);
        }
      } else if (newData[lineCode] && Array.isArray(newData[lineCode])) {
        // 其他路線類型：使用陣列
        newData[lineCode] = newData[lineCode].filter(id => id !== stationId);
        
        // 如果該路線沒有車站了，刪除該路線
        if (newData[lineCode].length === 0) {
          delete newData[lineCode];
        }
        
        setItem(STORAGE_KEY, newData);
      }
      
      return newData;
    });
  }, []);

  /**
   * 切換喜愛車站（如果存在則移除，不存在則添加）
   * @param {string} lineCode - 路線代碼
   * @param {string} stationId - 車站 ID
   * @param {string} route - 路線（僅 MTRBus 需要，如 'K12'）
   */
  const toggleStation = useCallback((lineCode, stationId, route = null) => {
    setSavedStations(prev => {
      const newData = { ...prev };
      
      // MTRBus 需要特殊處理：按路線分組
      if (lineCode === 'MTRBus' && route) {
        const { routeKey, baseRoute, variantId } = parseMTRBusRouteKey(route);
        if (!routeKey) return newData;

        if (!newData[lineCode]) {
          newData[lineCode] = {};
        }
        if (!newData[lineCode][routeKey]) {
          newData[lineCode][routeKey] = [];
        }

        const inVariant = newData[lineCode][routeKey].includes(stationId);
        const inLegacyBase = variantId && baseRoute && newData[lineCode][baseRoute]?.includes(stationId);

        if (inVariant || inLegacyBase) {
          // 移除（同時清理舊格式）
          newData[lineCode][routeKey] = newData[lineCode][routeKey].filter(id => id !== stationId);
          if (newData[lineCode][routeKey].length === 0) {
            delete newData[lineCode][routeKey];
          }

          if (variantId && baseRoute && baseRoute !== routeKey && newData[lineCode][baseRoute]) {
            newData[lineCode][baseRoute] = newData[lineCode][baseRoute].filter(id => id !== stationId);
            if (newData[lineCode][baseRoute].length === 0) {
              delete newData[lineCode][baseRoute];
            }
          }

          if (Object.keys(newData[lineCode]).length === 0) {
            delete newData[lineCode];
          }
        } else {
          // 添加
          newData[lineCode][routeKey] = [...newData[lineCode][routeKey], stationId];
        }
      } else {
        // 其他路線類型：使用陣列
        if (!newData[lineCode]) {
          newData[lineCode] = [];
        }
        
        const index = newData[lineCode].indexOf(stationId);
        
        if (index > -1) {
          // 移除
          newData[lineCode] = newData[lineCode].filter(id => id !== stationId);
          if (newData[lineCode].length === 0) {
            delete newData[lineCode];
          }
        } else {
          // 添加
          newData[lineCode] = [...newData[lineCode], stationId];
        }
      }
      
      setItem(STORAGE_KEY, newData);
      return newData;
    });
  }, []);

  /**
   * 檢查車站是否已儲存
   * @param {string} lineCode - 路線代碼
   * @param {string} stationId - 車站 ID
   * @param {string} route - 路線（僅 MTRBus 需要，如 'K12'）
   * @returns {boolean}
   */
  const isStationSaved = useCallback((lineCode, stationId, route = null) => {
    // MTRBus 需要特殊處理：按路線分組
    if (lineCode === 'MTRBus' && route) {
      const { routeKey, baseRoute } = parseMTRBusRouteKey(route);
      const byRouteKey = savedStations[lineCode]?.[routeKey]?.includes(stationId) || false;
      if (byRouteKey) return true;

      // 兼容舊資料：routeNo 儲存格式
      if (baseRoute && baseRoute !== routeKey) {
        const byBaseRoute = savedStations[lineCode]?.[baseRoute]?.includes(stationId) || false;
        if (byBaseRoute) return true;
      }

      // 兼容舊調用：若傳入 routeNo，檢查其所有變體鍵
      if (baseRoute && routeKey === baseRoute) {
        const variantMatched = Object.entries(savedStations[lineCode] || {}).some(([key, stations]) => {
          return key.startsWith(`${baseRoute}::`) && Array.isArray(stations) && stations.includes(stationId);
        });
        if (variantMatched) return true;
      }

      return false;
    }
    // 其他路線類型：使用陣列
    return savedStations[lineCode]?.includes(stationId) || false;
  }, [savedStations]);

  /**
   * 獲取指定路線的所有喜愛車站
   * @param {string} lineCode - 路線代碼
   * @param {string} route - 路線（僅 MTRBus 需要，如 'K12'）
   * @returns {string[]|object}
   */
  const getStationsByLine = useCallback((lineCode, route = null) => {
    // MTRBus 需要特殊處理：按路線分組
    if (lineCode === 'MTRBus' && route) {
      const { routeKey, baseRoute } = parseMTRBusRouteKey(route);
      const direct = savedStations[lineCode]?.[routeKey] || [];
      if (direct.length > 0) return direct;
      if (baseRoute && baseRoute !== routeKey) {
        return savedStations[lineCode]?.[baseRoute] || [];
      }
      return [];
    }
    if (lineCode === 'MTRBus') {
      // 返回所有路線的車站（物件格式）
      return savedStations[lineCode] || {};
    }
    // 其他路線類型：使用陣列
    return savedStations[lineCode] || [];
  }, [savedStations]);

  /**
   * 清空指定路線的所有喜愛車站
   * @param {string} lineCode - 路線代碼
   * @param {string} route - 路線（僅 MTRBus 需要，如 'K12'）
   */
  const clearLine = useCallback((lineCode, route = null) => {
    setSavedStations(prev => {
      const newData = { ...prev };
      
      // MTRBus 需要特殊處理：按路線分組
      if (lineCode === 'MTRBus' && route) {
        if (newData[lineCode]?.[route]) {
          delete newData[lineCode][route];
          // 如果 MTRBus 沒有路線了，刪除 MTRBus
          if (Object.keys(newData[lineCode]).length === 0) {
            delete newData[lineCode];
          }
        }
      } else {
        // 其他路線類型：直接刪除
        delete newData[lineCode];
      }
      
      setItem(STORAGE_KEY, newData);
      return newData;
    });
  }, []);

  /**
   * 清空所有喜愛車站
   */
  const clearAll = useCallback(() => {
    setSavedStations({});
    setItem(STORAGE_KEY, {});
  }, []);

  /**
   * 重新排序已儲存車站
   * @param {string} lineCode - 路線代碼
   * @param {string} fromStationId - 拖曳來源車站 ID
   * @param {string} toStationId - 放置目標車站 ID
   * @param {string} route - 路線（僅 MTRBus 需要，如 'K12'）
   */
  const reorderStations = useCallback((lineCode, fromStationId, toStationId, route = null) => {
    if (!lineCode || !fromStationId || !toStationId || fromStationId === toStationId) return;

    setSavedStations(prev => {
      const newData = { ...prev };

      const reorderArray = (arr) => {
        if (!Array.isArray(arr)) return arr;
        const fromIndex = arr.indexOf(fromStationId);
        const toIndex = arr.indexOf(toStationId);
        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return arr;

        const next = [...arr];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        return next;
      };

      if (lineCode === 'MTRBus' && route) {
        const current = newData?.[lineCode]?.[route];
        if (!current || !Array.isArray(current)) return prev;

        const nextRouteStations = reorderArray(current);
        if (nextRouteStations === current) return prev;

        newData[lineCode] = {
          ...newData[lineCode],
          [route]: nextRouteStations,
        };
      } else {
        const current = newData?.[lineCode];
        if (!current || !Array.isArray(current)) return prev;

        const nextStations = reorderArray(current);
        if (nextStations === current) return prev;

        newData[lineCode] = nextStations;
      }

      setItem(STORAGE_KEY, newData);
      return newData;
    });
  }, []);

  return {
    savedStations,
    addStation,
    removeStation,
    toggleStation,
    isStationSaved,
    getStationsByLine,
    clearLine,
    clearAll,
    reorderStations,
  };
};
